import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { ColumnFiltersState, SortingState } from "@tanstack/react-table";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import {
  fetchhsnCodes as OnfetchHsnCodes,
  updatehsnCode as onUpdateHsnCode,
  addhsnCode as onAddHsnCode,
  updateStatusInhsnCode as onUpdateStatusInHsnCode,
  exportHsnCodeCSV,
} from "slices/Masters/hsn-code/thunk";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { toast } from "react-toastify";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import ActionButton from "Common/Components/Buttons/ActionButton";
import ExportButton from "Common/Components/Buttons/ExportButton";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import ListingPageContainer from "Common/Components/PageContainer/ListingPageContainer";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { TruncatedTextWithTooltip } from "Common/Components/TruncatedTextWithTooltip";

const HSNCode = () => {
  document.title = "HSNCode List";
  const dispatch = useDispatch<any>();

  // Redux selector for hsn-code state
  const selectHsnCodes = createSelector(
    (state: any) => state.HSNCode,
    (hsnCode) => ({
      HsnCodes: hsnCode.hsnCodeList,
      rowCount: hsnCode.rowCount,
      loading: hsnCode.loading,
      error: hsnCode.error,
    })
  );

  const { HsnCodes, rowCount, loading, error } = useSelector(selectHsnCodes);

  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.master?.app_name,
    app_modules?.master?.modules?.hsn_code
  );

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(rowCount);
  const [tableInstance, setTableInstance] = useState<any>();
  // State for edit functionality
  const [editHsnCode, setEditHsnCode] = useState<boolean>(false);
  // const [columnFilters, setColumnFilters] = useState<any>();
  const [edit, setEdit] = useState<any>();
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [searchInput, setSearchInput] = useState("");
  // const [search, setSearch] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);

  const tableState = usePreservedTableState({
    key: TableKeys.MASTER.HSN_CODE,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  useEffect(() => {
    dispatch(
      OnfetchHsnCodes({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [dispatch, pageIndex, pageSize, sorting, search, filterValue]);


  useEffect(() => {
    setData(HsnCodes || []);
  }, [HsnCodes]);
  
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);
  // Handle edit action
  const handleEdit = (item: any) => {
    setEditHsnCode(!editHsnCode);
    setEdit({
      id: item.id,
      HsnCodeName: item.HsnCode_name,
      state: item.state_name,
      status: item.is_active,
      createdBy: item.created_by,
      updatedBy: item.updated_by,
      createDate: item.created_at,
      updatedDate: item.updated_at,
    });
  };
  const handleStatusToggle = async (pkg: any) => {
    try {
      const updatedStatus = !pkg.is_active;
      const updatedHsnCode = {
        id: pkg.hsn_code_id,
        is_active: updatedStatus,
      };
      await dispatch(onUpdateStatusInHsnCode(updatedHsnCode))
        .unwrap()
        .then(() => {
          toast.success(
            `HSN Code ${
              updatedStatus ? "activated" : "deactivated"
            } successfully`
          );
        });
      dispatch(
        OnfetchHsnCodes({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
        })
      );
    } catch (err: any) {
      toast.error("Failed to Update Status");
    }
  };

  // const DynamicAction = hsn_permissions?.permissions?.Update === true ? [{
  //   header: "Action",
  //   enableColumnFilter: false,
  //   enableSorting: false,
  //   cell: ({ row }: { row: any }) => (
  //     <ul className="d-flex gap-2 list-unstyled mb-0">
  //       <li>
  //         <Link
  //           to={`/edit-package/${row.original.id}`}
  //           className="btn btn-subtle-secondary btn-icon btn-sm edit-item-btn"
  //           onClick={() => handleEdit(row.original)}
  //         >
  //           <i className="ph-pencil"></i>
  //         </Link>
  //       </li>
  //     </ul>
  //   ),
  // },] : []

  const columns =
    // useMemo(
    //   () =>
    [
      {
        header: "Sr. No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT.MASTERS.HSN_CODE.FILTER_DROPDOWN("sr_no"),
      },
      {
        header: "HSN Code",
        accessorKey: "hsn_code",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT.MASTERS.HSN_CODE.FILTER_DROPDOWN("hsn_code"),
      },
      {
        header: "HSN Code Description",
        accessorKey: "hsn_code_description",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }: { row: any }) => (
          <div
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              width: "250px",
            }}
          >
            <TruncatedTextWithTooltip text={row.original.hsn_code_description} />
            {/* {row.original.hsn_code_description || "-"} */}
          </div>
        ),
        // filterType: "dropdown" as const,
        // filterFetchUrl: "hsn/dropdown/hsn_code_description/",
      },
      {
        header: "GST%",
        id: "gst__gst_percentage",
        accessorKey: "gst",
        enableColumnFilter: true,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT.MASTERS.HSN_CODE.FILTER_DROPDOWN(
          "gst__gst_percentage"
        ),
        cell: ({ row }: { row: any }) => `${row.original.gst?.gst_percentage}%`,
      },
      {
        header: "Status",
        accessorKey: "is_active",
        enableColumnFilter: false,
        enableSorting: false,
        //  filterType: "dropdown" as const,
        // filterFetchUrl: "/gst/GSTdropdown/is_active",
        cell: ({ row }: { row: any }) => {
          const pkg = row.original;

          return (
            <div
              style={{
                fontSize: "1.5rem",
                cursor: canUpdate ? "pointer" : "not-allowed",
              }}
              onClick={() => canUpdate && handleStatusToggle(pkg)}
            >
              <i
                className={`bi ${
                  pkg.is_active
                    ? "bi-toggle-on text-success"
                    : "bi-toggle-off text-danger"
                }`}
              />
            </div>
          );
        },
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        filterFetchUrl:
          API_ENDPOINT.MASTERS.HSN_CODE.FILTER_DROPDOWN("created_at"),
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.created_by_user?.username}
            date={row.original.created_at}
          />
        ),
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        filterFetchUrl: "/hsn/dropdown/updated_at",
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.updated_by_user?.username}
            date={row.original.updated_at}
          />
        ),
      },
      ...(canUpdate
        ?
      [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const menuItems = [
            {
              icon: EditIcon,
              label: "Edit",
              permission: true,
              link: `/master/edit-hsn-code/${row.original.hsn_code_id}`,
              color: "primary",
            },
          ];
          return <DynamicMenuItem row={row.original} menuItems={menuItems} />;
        },
      },
      ]
      : []),
    ];
  //   [dispatch, HsnCodes]
  // );

  function handleChange(e: any): void {
    setSearchInput(e.target.value);
  }

  return (
    <ListingPageContainer>
      <Row id="invoiceList">
        <Col lg={12}>
          <Card className="shadow-none">
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">HSN Code</h6>
                </Col>
                <Row className="align-items-center justify-content-between mt-1 px-0 g-3 flex-wrap">
                  {/* Search Box */}
                  <SearchBox
                    value={searchInput}
                    onChange={(e: any) => setSearchInput(e.target.value)}
                    placeholder="Search..."
                  />

                  {/* Column Visibility + Actions */}
                  <Col xs={12} md={12} lg={8} xl={6} className="">
                    <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 w-100">
                      {/* Left side: Column Visibility */}
                      <div className="">
                        {tableInstance && (
                          <ColumnVisibilityDropdown
                            columns={tableInstance.getAllLeafColumns()}
                            excludedColumns={["action", "sr_no"]}
                          />
                        )}
                      </div>

                      <div className="hstack gap-2">
                        {canView && (
                          <ExportButton
                            onExport={async () => {
                              return await exportHsnCodeCSV({
                                search,
                                filters: filterValue,
                              });
                            }}
                          />
                        )}
                      </div>
                      <div>
                        {canCreate && (
                          <ActionButton
                            label="Add HSN"
                            icon="bi bi-plus-circle"
                            navigateLink="/master/add-hsn-code"
                            variant="primary"
                          />
                        )}
                      </div>
                    </div>
                  </Col>
                </Row>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper loading={loading} error={error}>
                <MasterTableContainer
                  columns={columns}
                  data={data}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isBordered={false}
                  isPagination={true}
                  manualSorting={true}
                  colValue={setFilterValue}
                  onTableReady={setTableInstance}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default HSNCode;
